import React, { ChangeEvent, useRef, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import Papa from 'papaparse';
import { useForm } from 'react-hook-form';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
  styled,
} from '@mui/material';

import CorporateEventsService, { CreateInvitedUserParams } from '~/services/CorporateEventsService';

const ExampleContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
  fontFamily: 'monospace',
  fontSize: '0.875rem',
}));

const CodeBlock = styled('pre')(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  overflow: 'auto',
  border: `1px solid ${theme.palette.divider}`,
}));

const ExampleLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 600,
  display: 'block',
  marginBottom: theme.spacing(0.5),
}));

const ExampleNote = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: 'block',
  marginTop: theme.spacing(1.5),
}));

interface ImportInvitedUsersDialogProps {
  open: boolean;
  eventId: string;
  onClose: () => void;
}

export default function ImportInvitedUsersDialog({ open, eventId, onClose }: ImportInvitedUsersDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const importMutation = useMutation({
    mutationFn: (users: Array<CreateInvitedUserParams>) => CorporateEventsService.upsertInvitedUsers(eventId, users),
    onSuccess: (importedUsers) => {
      queryClient.invalidateQueries({ queryKey: ['corporate-events', 'invited-users', eventId] });
      enqueueSnackbar(`Successfully imported ${importedUsers.length} user(s)`, { variant: 'success' });
      handleClose();
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error importing users: ${error.message}`, { variant: 'error' });
    },
  });

  const { handleSubmit } = useForm();

  const handleDownloadTemplate = () => {
    const csvContent = [
      'email,fullName,role,notes,analyticsAccess',
      'john.doe@example.com,John Doe,Manager,Important guest,false',
      'jane.smith@example.com,Jane Smith,Director,,true',
      'bob.jones@example.com,Bob Jones,,,false',
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'invited_users_template.csv';
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const parseCSVFile = (file: File): Promise<Array<CreateInvitedUserParams>> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (evt) => {
        if (!evt?.target?.result) {
          reject(new Error('Failed to read file'));
          return;
        }

        const { result } = evt.target;
        if (typeof result !== 'string') {
          reject(new Error('File must be a text file'));
          return;
        }

        try {
          const parseResult = Papa.parse<Record<string, string>>(result, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => header.trim().toLowerCase(),
          });

          const { data, errors } = parseResult;

          if (errors.length > 0) {
            reject(new Error(`CSV parsing errors: ${errors.map((e) => e.message).join(', ')}`));
            return;
          }

          if (data.length === 0) {
            reject(new Error('CSV file is empty or has no valid data rows'));
            return;
          }

          // Validate and transform data
          const users: Array<CreateInvitedUserParams> = [];
          const validationErrors: Array<string> = [];

          data.forEach((row, index) => {
            const email = row.email?.trim().toLowerCase();
            if (!email) {
              validationErrors.push(`Row ${index + 2}: Email is required`);
              return;
            }

            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
              validationErrors.push(`Row ${index + 2}: Invalid email format: ${email}`);
              return;
            }

            const analyticsAccessRaw = row.analyticsaccess?.trim().toLowerCase();
            const hasAnalyticsAccess =
              analyticsAccessRaw === 'true' || analyticsAccessRaw === 'yes'
                ? true
                : analyticsAccessRaw === 'false' || analyticsAccessRaw === 'no'
                  ? false
                  : undefined;

            users.push({
              email,
              fullName: row.fullname?.trim() || row['full name']?.trim() || undefined,
              role: row.role?.trim() || undefined,
              notes: row.notes?.trim() || undefined,
              hasAnalyticsAccess,
            });
          });

          if (validationErrors.length > 0) {
            reject(new Error(`Validation errors:\n${validationErrors.join('\n')}`));
            return;
          }

          if (users.length === 0) {
            reject(new Error('No valid users found in file'));
            return;
          }

          resolve(users);
        } catch (error) {
          reject(new Error(`Failed to parse CSV: ${error instanceof Error ? error.message : 'Unknown error'}`));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  };

  const handleImport = async () => {
    if (!selectedFile) {
      enqueueSnackbar('Please select a file', { variant: 'warning' });
      return;
    }

    setIsProcessing(true);
    try {
      const users = await parseCSVFile(selectedFile);
      await importMutation.mutateAsync(users);
    } catch (error) {
      enqueueSnackbar(`Error processing file: ${error instanceof Error ? error.message : 'Unknown error'}`, {
        variant: 'error',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleClose}
      component="form"
      onSubmit={handleSubmit(handleImport)}
    >
      <DialogTitle>Import Invited Users from File</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Instructions:</strong> Upload a CSV file with the following columns:
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li>
                  <strong>email</strong> (required) – User email address
                </li>
                <li>
                  <strong>fullName</strong> (optional) – User full name
                </li>
                <li>
                  <strong>role</strong> (optional) – User role
                </li>
                <li>
                  <strong>notes</strong> (optional) – Additional notes
                </li>
                <li>
                  <strong>analyticsAccess</strong> (optional) – Grant analytics access: <code>true</code> or{' '}
                  <code>false</code>
                </li>
              </ul>
            </Typography>
          </Alert>

          <Box>
            <ExampleContainer variant="outlined">
              <Box sx={{ mb: 1 }}>
                <ExampleLabel variant="caption">Example CSV format:</ExampleLabel>
              </Box>
              <CodeBlock>
                <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>
                  email,fullName,role,notes,analyticsAccess
                </Box>
                {'\n'}
                <Box component="span" sx={{ color: 'text.primary' }}>
                  john.doe@example.com,John Doe,Manager,Important guest,false
                </Box>
                {'\n'}
                <Box component="span" sx={{ color: 'text.primary' }}>
                  jane.smith@example.com,Jane Smith,Director,,true
                </Box>
                {'\n'}
                <Box component="span" sx={{ color: 'text.primary' }}>
                  bob.jones@example.com,Bob Jones,,,false
                </Box>
              </CodeBlock>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 1.5 }}>
                <ExampleNote variant="caption" sx={{ mt: 0 }}>
                  • First row must be the column headers
                  <br />• Email is required, other fields are optional
                  <br />• Supports CSV (.csv) format only
                </ExampleNote>
                <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
                  Download Template
                </Button>
              </Box>
            </ExampleContainer>
          </Box>

          <Box>
            <Button
              component="label"
              role={undefined}
              variant="contained"
              tabIndex={-1}
              disabled={!!selectedFile}
              startIcon={selectedFile ? <CheckBoxIcon /> : <CloudUploadIcon />}
            >
              {selectedFile ? `File selected: ${selectedFile.name}` : 'Choose File'}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                style={{
                  clip: 'rect(0 0 0 0)',
                  clipPath: 'inset(50%)',
                  height: 1,
                  overflow: 'hidden',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  whiteSpace: 'nowrap',
                  width: 1,
                }}
              />
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importMutation.isPending || isProcessing}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" disabled={!selectedFile || importMutation.isPending || isProcessing}>
          {isProcessing ? 'Processing...' : importMutation.isPending ? 'Importing...' : 'Import Users'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
